import React, { useMemo } from "react";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DialogContent from "@mui/material/DialogContent";
import { useQuery } from "@apollo/client/react";
import CircularProgress from "@mui/material/CircularProgress";

import {
  fetchAssetValues,
  prepareAssetInputWithCustomFields,
} from "../../../../../../../utils/asset";
import {
  ASSET_QUERY,
  REMOVE_ASSET_MUTATION,
  UPDATE_ASSET_MUTATION,
  ADD_ASSET_IMAGE_MUTATION,
} from "../../../../../../../constants/graphql";
import {
  DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
  UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
} from "../../../../../../../constants/graphql/mutations";
import { INTERNAL_WORK_ORDER_QUERY } from "../../../../../../../constants/graphql/queries";

import useMutation from "../../../../../../../hooks/useMutation";
import ConfirmDeleteModal from "../../../../../../../components/images/ConfirmDeleteModal";
import useQueryParams from "../../../../../../../hooks/useQueryParams";
import { fullWidthDialog } from "../../../../../../../constants/dialog";
import { AssetsCollectionProvider } from "../../../../../../../contexts/assetsCollection";
import FormPage from "./FormPage";

export default function ItemDetails({
  id,
  project,
  internalWorkOrder,
  internalWorkOrderItem,
  reFetch,
  onClose,
  sameLocation,
}) {
  const params = useQueryParams();
  const internalWorkOrderId = params.get("id");
  const internalWorkOrderItemId = params.get("internalWorkOrderItemId");
  const bulkDuplicateModal = params.get("bulkDuplicateModal");
  const [openRemoveItem, setOpenRemoveItem] = React.useState(false);
  const { data: { asset = {} } = {}, loading } = useQuery(ASSET_QUERY, {
    variables: { id },
  });

  const [updateAsset, { loading: updatingAsset }] = useMutation(UPDATE_ASSET_MUTATION);
  const [createImage] = useMutation(ADD_ASSET_IMAGE_MUTATION);
  const [removeAsset, { loading: removingAsset }] = useMutation(REMOVE_ASSET_MUTATION, {
    refetchQueries: [{ query: INTERNAL_WORK_ORDER_QUERY, variables: { id: internalWorkOrderId } }],
  });
  const [removeInternalWorkOrderItem, { loading: removingInternalWorkOrderItem }] = useMutation(
    DELETE_INTERNAL_WORK_ORDER_ITEM_MUTATION,
    {
      refetchQueries: [
        { query: INTERNAL_WORK_ORDER_QUERY, variables: { id: internalWorkOrderId } },
      ],
    }
  );
  const [updateInternalWorkOrderItem] = useMutation(UPDATE_INTERNAL_WORK_ORDER_ITEM_MUTATION, {
    refetchQueries: [{ query: INTERNAL_WORK_ORDER_QUERY, variables: { id: internalWorkOrderId } }],
  });

  const currentInternalWorkOrderItem = internalWorkOrder?.internalWorkOrderItems?.find(
    item => item.id === internalWorkOrderItemId
  );
  const internalWorkOrderItemQuantity = currentInternalWorkOrderItem?.quantity || 0;
  const itemComment = currentInternalWorkOrderItem?.comment;

  const initialValues = useMemo(
    () => ({
      ...fetchAssetValues(asset),
      availableQuantity: asset.availableQuantity,
      internalWorkOrderItemQuantity,
      itemComment,
    }),
    [asset, internalWorkOrderItemQuantity, itemComment]
  );

  const removing = removingAsset || removingInternalWorkOrderItem;
  const loadingMutation = updatingAsset;

  const handleSave = async ({
    attachments,
    availableQuantity,
    internalWorkOrderItemQuantity,
    itemComment,
    ...values
  }) => {
    const rackLocationsChanges = params.get("rackLocationsChanges")
      ? JSON.parse(params.get("rackLocationsChanges"))
      : [];

    const internalWorkOrderItemInput = {
      quantity: internalWorkOrderItemQuantity,
      comment: itemComment,
      rackLocations: rackLocationsChanges.length
        ? rackLocationsChanges
        : internalWorkOrderItem.rackLocations,
    };

    const input = prepareAssetInputWithCustomFields(values, asset);

    await updateInternalWorkOrderItem({
      variables: { id: internalWorkOrderItemId, input: internalWorkOrderItemInput },
    });
    const response = await updateAsset({ variables: { id, input } });
    if (bulkDuplicateModal !== "true") {
      onClose();
    }

    return response;
  };

  const handleRemoveAsset = async () => {
    if (project.importedAssets) {
      await removeInternalWorkOrderItem({ variables: { id: internalWorkOrderItemId } });
    } else {
      await removeAsset({ variables: { id: asset.id } });
    }
    onClose();
  };

  const handleClose = () => {
    const currentParams = new URLSearchParams(window.location.search);
    const currentAddInternalWorkOrderItem =
      currentParams.get("addInternalWorkOrderItem") === "true";

    if (currentAddInternalWorkOrderItem) {
      toggleRemoveItem();
    } else {
      onClose();
    }
  };

  const toggleRemoveItem = () => setOpenRemoveItem(prev => !prev);

  return (
    <Dialog open fullWidth maxWidth="xs" onClose={handleClose} sx={fullWidthDialog}>
      <DialogTitle sx={{ background: "#f4f6f8", display: "flex", justifyContent: "space-between" }}>
        <Box width="48px" />
        <Typography fontSize={24} fontWeight={500} color="textPrimary">
          Item Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ background: "#f4f6f8" }}>
        {loading ? (
          <Box height={258} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box>
            <AssetsCollectionProvider variables={{ projectId: project?.id }} skipQuery>
              <Formik key={asset?.id} initialValues={initialValues} onSubmit={handleSave}>
                <FormPage
                  type="internal"
                  internalWorkOrderId={internalWorkOrder.id}
                  internalWorkOrder={internalWorkOrder}
                  currentAsset={asset}
                  onCloseModal={onClose}
                  handeRefetch={reFetch}
                  internalWorkOrderItem={internalWorkOrderItem}
                  removing={removing}
                  toggleRemoveItem={toggleRemoveItem}
                  sameLocation={sameLocation}
                  project={project}
                  closeItemDetails={handleClose}
                  loadingMutation={loadingMutation}
                  updateAsset={updateAsset}
                  createImage={createImage}
                />
              </Formik>
            </AssetsCollectionProvider>
          </Box>
        )}
      </DialogContent>
      {openRemoveItem && (
        <ConfirmDeleteModal
          title="Delete Item"
          descr="Are you sure want to delete the selected item?"
          onClose={toggleRemoveItem}
          onDelete={handleRemoveAsset}
        />
      )}
    </Dialog>
  );
}
